import React, { useState, useEffect, useRef } from 'react';
import { getCompanyFormOptions, createCompany, updateCompany } from '../services/api';
import { useToast } from '../context/ToastContext';
import './CompanyForm.css';

function CompanyForm({ company, onSuccess, onClose }) {
  const { toast } = useToast();
  const isEditing = !!company;
  
  const [formData, setFormData] = useState({
    name: '',
    abbreviation: '',
    industry: '',
    company_type: '',
    head_office_location: '',
    zone: '',
    continent: '',
    region: '',
    website: '',
    linkedin_url: '',
    is_client: false,
    potential_flag: '',
    about: '',
  });
  
  const [options, setOptions] = useState({
    industries: [],
    company_types: [],
    zones: [],
    continents: [],
    regions: [],
  });
  
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const successTimeoutRef = useRef(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const initialData = company ? {
    name: company.name || '',
    abbreviation: company.abbreviation || '',
    industry: company.industry || '',
    company_type: company.company_type || '',
    head_office_location: company.head_office_location || '',
    zone: company.zone || '',
    continent: company.continent || '',
    region: company.region || '',
    website: company.website || '',
    linkedin_url: company.linkedin_url || '',
    is_client: company.is_client || false,
    potential_flag: company.potential_flag || '',
    about: company.about || '',
  } : null;

  const isDirty = initialData
    ? Object.keys(initialData).some((k) => String(formData[k] || '') !== String(initialData[k] || ''))
    : (formData.name || formData.abbreviation || formData.industry || formData.company_type || formData.head_office_location || formData.zone || formData.continent || formData.region || formData.website || formData.linkedin_url || formData.about || formData.is_client || formData.potential_flag);

  const handleRequestClose = () => {
    if (isDirty && !showDiscardConfirm) {
      setShowDiscardConfirm(true);
      return;
    }
    setShowDiscardConfirm(false);
    onClose();
  };

  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  useEffect(() => {
    return () => { if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current); };
  }, []);

  // Escape to close; scroll lock when modal open
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (showDiscardConfirm) {
        setShowDiscardConfirm(false);
        return;
      }
      if (isDirty) setShowDiscardConfirm(true);
      else onClose();
    };
    document.addEventListener('keydown', handleEscape);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = prevOverflow;
    };
  }, [showDiscardConfirm, isDirty, onClose]);

  // Load form options and existing data
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const response = await getCompanyFormOptions();
        if (response.success) {
          setOptions(response.options);
        }
      } catch (err) {
        console.error('Failed to load form options:', err);
      } finally {
        setOptionsLoading(false);
      }
    };
    loadOptions();

    // If editing, populate form with existing data
    if (company) {
      setFormData({
        name: company.name || '',
        abbreviation: company.abbreviation || '',
        industry: company.industry || '',
        company_type: company.company_type || '',
        head_office_location: company.head_office_location || '',
        zone: company.zone || '',
        continent: company.continent || '',
        region: company.region || '',
        website: company.website || '',
        linkedin_url: company.linkedin_url || '',
        is_client: company.is_client || false,
        potential_flag: company.potential_flag || '',
        about: company.about || '',
      });
    }
  }, [company]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError(null);
    setFieldErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // Validate required fields
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Company name is required';
    if (!formData.industry) errors.industry = 'Industry is required';
    if (!formData.company_type) errors.company_type = 'Company type is required';
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        name: formData.name.trim(),
        abbreviation: (formData.abbreviation || '').trim().toUpperCase() || null,
        head_office_location: (formData.head_office_location || '').trim() || null,
        zone: formData.zone || null,
        continent: formData.continent || null,
        region: formData.region || null,
        website: (formData.website || '').trim() || null,
        linkedin_url: (formData.linkedin_url || '').trim() || null,
        potential_flag: (formData.potential_flag || '').trim() || null,
        about: (formData.about || '').trim() || null,
      };

      let response;
      if (isEditing) {
        response = await updateCompany(company.id, dataToSend);
      } else {
        response = await createCompany(dataToSend);
      }

      if (response.success) {
        setLoading(false);
        setSubmitSuccess(true);
        toast.success(isEditing ? 'Company updated' : 'Company added');
        successTimeoutRef.current = setTimeout(() => onSuccess(), 1200);
        return;
      }
    } catch (err) {
      if (err.details && typeof err.details === 'object') {
        setFieldErrors(err.details);
      } else {
        setError(err.message || 'Failed to save company');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleRequestClose()}>
      <div className="company-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Company' : 'Add New Company'}</h2>
          <button type="button" className="close-btn" onClick={handleRequestClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {optionsLoading ? (
          <div className="form-loading">
            <div className="spinner"></div>
            <p>Loading form...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="company-form">
            {error && (
              <div className="form-error">
                {error}
              </div>
            )}

            <div className="form-row name-abbr-row">
              <div className="form-group name-group field-required">
                <label htmlFor="name">Company Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  required
                  aria-required="true"
                  aria-invalid={!!fieldErrors.name}
                />
                {fieldErrors.name && <span className="field-error" role="alert">{fieldErrors.name}</span>}
              </div>

              <div className="form-group abbreviation-group">
                <label htmlFor="abbreviation">Abbreviation</label>
                <input
                  type="text"
                  id="abbreviation"
                  name="abbreviation"
                  value={formData.abbreviation}
                  onChange={handleChange}
                  placeholder="e.g., RIL"
                  maxLength="10"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group field-required">
                <label htmlFor="industry">Industry / Sector</label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  aria-invalid={!!fieldErrors.industry}
                >
                  <option value="">Select Industry</option>
                  {options.industries.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.industry && <span className="field-error" role="alert">{fieldErrors.industry}</span>}
              </div>

              <div className="form-group field-required">
                <label htmlFor="company_type">Company Type</label>
                <select
                  id="company_type"
                  name="company_type"
                  value={formData.company_type}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  aria-invalid={!!fieldErrors.company_type}
                >
                  <option value="">Select Type</option>
                  {options.company_types.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.company_type && <span className="field-error" role="alert">{fieldErrors.company_type}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="head_office_location">Head Office Location</label>
                <input
                  type="text"
                  id="head_office_location"
                  name="head_office_location"
                  value={formData.head_office_location}
                  onChange={handleChange}
                  placeholder="e.g., New Delhi, Mumbai"
                />
              </div>

              <div className="form-group">
                <label htmlFor="zone">Zone (India)</label>
                <select
                  id="zone"
                  name="zone"
                  value={formData.zone}
                  onChange={handleChange}
                >
                  <option value="">Select Zone</option>
                  {options.zones.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="continent">Continent</label>
                <select
                  id="continent"
                  name="continent"
                  value={formData.continent}
                  onChange={handleChange}
                >
                  <option value="">Select Continent</option>
                  {(options.continents || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="region">Region</label>
                <select
                  id="region"
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                >
                  <option value="">Select Region</option>
                  {(options.regions || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="website">Official Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="linkedin_url">LinkedIn Company Page</label>
              <input
                type="text"
                id="linkedin_url"
                name="linkedin_url"
                value={formData.linkedin_url}
                onChange={handleChange}
                placeholder="https://www.linkedin.com/company/..."
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_client"
                  checked={formData.is_client}
                  onChange={handleChange}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">Existing Client</span>
              </label>
              <span className="checkbox-hint">Check if this company is an existing client</span>
            </div>

            {!formData.is_client && (
              <div className="form-group">
                <label htmlFor="potential_flag">Potential client flag</label>
                <select
                  id="potential_flag"
                  name="potential_flag"
                  value={formData.potential_flag || ''}
                  onChange={handleChange}
                >
                  <option value="">None</option>
                  <option value="red">Hot (Red)</option>
                  <option value="yellow">Warm (Yellow)</option>
                  <option value="green">Cold (Green)</option>
                </select>
                <span className="checkbox-hint">Row highlight on Client List for potential clients</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="about">About Company</label>
              <textarea
                id="about"
                name="about"
                value={formData.about}
                onChange={handleChange}
                placeholder="Brief description about the company, services, key contacts, etc."
                rows="4"
              />
            </div>

            <div className="form-actions form-actions-sticky">
              <button type="button" className="cancel-btn" onClick={handleRequestClose} disabled={submitSuccess}>
                Cancel
              </button>
              <button type="submit" className={`submit-btn ${loading ? 'btn-loading' : ''} ${submitSuccess ? 'btn-success-state' : ''}`} disabled={loading || submitSuccess} aria-busy={loading}>
                {loading ? (
                  <span className="btn-spinner"></span>
                ) : submitSuccess ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18" aria-hidden>
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Saved
                  </>
                ) : isEditing ? (
                  'Update Company'
                ) : (
                  'Add Company'
                )}
              </button>
            </div>
          </form>
        )}

        {showDiscardConfirm && (
          <div className="discard-confirm-overlay">
            <div className="discard-confirm-box">
              <p>Discard unsaved changes?</p>
              <div className="discard-confirm-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowDiscardConfirm(false)}>
                  Keep editing
                </button>
                <button type="button" className="confirm-discard-btn" onClick={handleConfirmDiscard}>
                  Discard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyForm;
